"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { ListingCard } from "@/components/marketplace/listing-card";
import { getReleaseListingsQueryOptions } from "@/components/marketplace/release-listings-query";
import { getWorkListingsQueryOptions } from "@/components/marketplace/work-listings-query";
import { useCurrency } from "@/hooks/use-currency";
import { useIsPartnerShare } from "@/hooks/use-is-partner-share";

type SortMode = "grade" | "price" | "newest";
interface MarketplaceListing {
  id: string;
  salePrice: string | null;
  saleCurrency: string | null;
  recordGrade?: string | null;
  sleeveGrade?: string | null;
  saleNotes?: string | null;
  salePhotos?: string[] | null;
  sellerName: string | null;
  sellerUsername?: string | null;
  sellerAvatarUrl?: string | null;
  sellerCountry?: string | null;
  saleCreatedAt?: string | Date | null;
}

interface WorkMarketplaceSectionProps {
  workId: string;
}

interface ReleaseMarketplaceSectionProps {
  releaseId: string;
}

function MarketplaceSectionShell({ children }: { children: ReactNode }) {
  if (useIsPartnerShare()) return null;
  return <div className="mt-4">{children}</div>;
}
function WorkListingsSectionContent({ workId }: WorkMarketplaceSectionProps) {
  const { currency } = useCurrency();
  const query = useQuery({
    ...getWorkListingsQueryOptions({ workId, currency, enabled: workId.length > 0 }),
  });

  return (
    <ListingsSectionContent
      surface="work_detail"
      isLoading={query.isLoading}
      rawListings={(query.data ?? []) as MarketplaceListing[]}
    />
  );
}

function ReleaseListingsSectionContent({ releaseId }: ReleaseMarketplaceSectionProps) {
  const { currency } = useCurrency();
  const query = useQuery({
    ...getReleaseListingsQueryOptions({ releaseId, currency, enabled: releaseId.length > 0 }),
  });

  return (
    <ListingsSectionContent
      surface="release_detail"
      isLoading={query.isLoading}
      rawListings={(query.data ?? []) as MarketplaceListing[]}
    />
  );
}

export function WorkMarketplaceSection({ workId }: WorkMarketplaceSectionProps) {
  return (
    <MarketplaceSectionShell>
      <WorkListingsSectionContent workId={workId} />
    </MarketplaceSectionShell>
  );
}

export function ReleaseMarketplaceSection({ releaseId }: ReleaseMarketplaceSectionProps) {
  return (
    <MarketplaceSectionShell>
      <ReleaseListingsSectionContent releaseId={releaseId} />
    </MarketplaceSectionShell>
  );
}

function ListingsSectionContent({
  rawListings,
  isLoading,
  surface,
}: {
  rawListings: MarketplaceListing[];
  isLoading: boolean;
  surface: string;
}) {
  const t = useTranslations("shared.marketplace");
  const { currency } = useCurrency();
  const [sortMode, setSortMode] = useState<SortMode>("grade");

  const listings = useMemo(() => {
    // Data already arrives sorted by grade then price from DB.
    // Re-sort only if user picks a different sort mode.
    if (sortMode === "grade") return rawListings;

    const copy = [...rawListings];
    if (sortMode === "price") {
      copy.sort((a, b) => {
        const pa = Number(a.salePrice ?? 0);
        const pb = Number(b.salePrice ?? 0);
        return pa - pb;
      });
    } else if (sortMode === "newest") {
      copy.sort((a, b) => {
        const da = a.saleCreatedAt ? new Date(a.saleCreatedAt).getTime() : 0;
        const db = b.saleCreatedAt ? new Date(b.saleCreatedAt).getTime() : 0;
        return db - da;
      });
    }
    return copy;
  }, [rawListings, sortMode]);

  const count = listings.length;

  return (
    <div id="listings-section">
      {/* Header — only show when there are listings */}
      {(isLoading || count > 0) && (
        <div className="flex items-center justify-between border-t border-border pt-4 pb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {count > 0 ? t("listingsSection", { count }) : t("listingsSectionEmpty")}
          </h3>

          {/* Sort buttons */}
          {count > 1 && (
            <div className="flex items-center gap-1 text-xs">
              <SortButton
                active={sortMode === "grade"}
                onClick={() => {
                  setSortMode("grade");
                }}
                label={t("sortByGrade")}
              />
              <SortButton
                active={sortMode === "price"}
                onClick={() => {
                  setSortMode("price");
                }}
                label={t("sortByPrice")}
              />
              <SortButton
                active={sortMode === "newest"}
                onClick={() => {
                  setSortMode("newest");
                }}
                label={t("sortByNewest")}
              />
            </div>
          )}
        </div>
      )}
      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex items-center gap-4 p-4 rounded-lg bg-secondary/50 border border-border animate-pulse"
            >
              <div className="w-16 h-16 rounded-md bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-24 bg-muted rounded" />
                <div className="h-3 w-32 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Listings */}
      {!isLoading && count > 0 && (
        <ul className="space-y-3">
          {listings.map((listing) => (
            <li key={listing.id}>
              <ListingCard
                id={listing.id}
                salePrice={listing.salePrice ?? "0"}
                saleCurrency={listing.saleCurrency ?? currency}
                recordGrade={listing.recordGrade}
                sleeveGrade={listing.sleeveGrade}
                saleNotes={listing.saleNotes}
                salePhotos={listing.salePhotos}
                sellerName={listing.sellerName}
                sellerUsername={listing.sellerUsername}
                sellerAvatarUrl={listing.sellerAvatarUrl}
                sellerCountry={listing.sellerCountry}
                saleCreatedAt={listing.saleCreatedAt ? String(listing.saleCreatedAt) : null}
                analyticsSurface={surface}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SortButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded transition-colors ${
        active
          ? "bg-primary/20 text-primary font-semibold"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
